import React, { useEffect } from "react";
import toastr from "toastr";
import {
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
} from "@material-ui/core";
import Input from "@mui/material/Input";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"; // ✅ ADDED
import GetAppIcon from "@mui/icons-material/GetApp";
import { makeDELETERequestOnUserServiceWithQueryParameter } from "../../Api/api";
import UserHelpers from "./UserHelpers";

const BankDetailsInformationGrid = (props) => {
  const isNRI = props.accountType === "individual_non_resident" || props.currentUser?.accountType === "individual_non_resident";

  const initialState = {
    bankName: props?.bankDetails?.bankName || "",
    beneficiaryName: props?.bankDetails?.beneficiaryName || "",
    accountNumber: props?.bankDetails?.accountNumber || "",
    ifscCode: props?.bankDetails?.ifscCode || "",
    branchName: props?.bankDetails?.branchName || "",
    kycPassword: props?.bankDetails?.kycPassword || "",
    accountType: props?.bankDetails?.accountType || "savings",
    nriBankAccountType: props?.bankDetails?.nriBankAccountType || "",
    pisType: props?.bankDetails?.pisType || "",
    nriBankProof: null,
    nriBankProofFilename: props?.bankDetails?.nriBankProofFilename || "",
    nriBankProofFile: props?.bankDetails?.nriBankProofFile || props?.bankDetails?.nri_bank_proof_filename_file || "",
  };

  const [currentState, setCurrentState] = React.useState(initialState);

  const [createCond] = React.useState(
    props.bankDetails?.accountNumber === ""
  );

  const [selectedFile, setSelectedFile] = React.useState(null);

  const [base64, setBase64] = React.useState(
    props?.bankDetails?.image || ""
  );

  useEffect(() => {
    if (selectedFile !== null) {
      const reader = new FileReader();
      reader.onload = () =>
        setBase64(UserHelpers.removePrefix(reader.result));
      reader.readAsDataURL(UserHelpers.convertedToBlob(selectedFile));
    }
  }, [selectedFile]);

  const addBankDetails = (event) => {
    event.preventDefault();
    props.addBankDetails(event, currentState, selectedFile);
  };

  const saveBankDetails = (event) => {
    event.preventDefault();
    props.saveBankDetails(event, currentState, selectedFile);
  };

  function deleteBankDetails(event) {
    event.preventDefault();

    if (!currentState.accountNumber) {
      toastr.error("Account number is missing");
      return;
    }

    makeDELETERequestOnUserServiceWithQueryParameter(
      `/kyc/bank_detail/delete/${props.currentUser.uuid}/${currentState.accountNumber}/`
    )
      .then((response) => {
        if (response?.status === 204) {
          toastr.success("Deleted bank details !");
          props.nullifyBankDetails(event, currentState.accountNumber);
        }
      })
      .catch((error) => toastr.error(error));
  }

  const downloadNriProof = () => {
    const base64Data = currentState.nriBankProofFile || props?.bankDetails?.nri_bank_proof_filename_file;

    if (!base64Data) {
      toastr.error("No file available for download");
      return;
    }

    const cleanBase64 = base64Data.replace(/^data:image\/\w+;base64,/, "").replace(/^data:application\/pdf;base64,/, "").replace(/\s/g, "");
    const isPdf = cleanBase64.startsWith("JVBERi0");
    const mimeType = isPdf ? "application/pdf" : "image/png";
    const extension = isPdf ? "pdf" : "png";

    try {
      const byteCharacters = atob(cleanBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = currentState.nriBankProofFilename || props?.bankDetails?.nri_bank_proof_filename || `nri_bank_proof.${extension}`;
      if (isPdf) link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error("Download error:", error);
      toastr.error("Unable to download file. The file may be corrupted.");
    }
  };

  const handleInputChange = (field, value) => {
    setCurrentState((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const handleNriProofChange = (event) => {
    const file = event.target.files[0];
    setCurrentState((prev) => ({ ...prev, nriBankProof: file }));
  };

  return (
    <>
      <TextField label="Bank Name" variant="outlined" fullWidth margin="dense" value={currentState.bankName} onChange={(e) => handleInputChange("bankName", e.target.value)} />
      <TextField label="Beneficiary Name" variant="outlined" fullWidth margin="dense" value={currentState.beneficiaryName} onChange={(e) => handleInputChange("beneficiaryName", e.target.value)} />
      <TextField label="Branch Name" variant="outlined" fullWidth margin="dense" value={currentState.branchName} onChange={(e) => handleInputChange("branchName", e.target.value)} />
      <TextField label="Account Number" variant="outlined" fullWidth margin="dense" value={currentState.accountNumber} onChange={(e) => handleInputChange("accountNumber", e.target.value)} />
      <TextField label="IFSC Code" variant="outlined" fullWidth margin="dense" value={currentState.ifscCode} onChange={(e) => handleInputChange("ifscCode", e.target.value)} />

      <FormControl variant="outlined" fullWidth margin="dense">
        <InputLabel id="account-type-label">Account Type</InputLabel>
        <Select labelId="account-type-label" value={currentState.accountType} onChange={(e) => handleInputChange("accountType", e.target.value)} label="Account Type">
          <MenuItem value="savings">Savings</MenuItem>
          <MenuItem value="current">Current</MenuItem>
        </Select>
      </FormControl>

      <TextField label="Password - If Blank, then no password is set by client" variant="outlined" fullWidth margin="dense" value={currentState.kycPassword} onChange={(e) => handleInputChange("kycPassword", e.target.value)} />

      {!isNRI && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Upload Cancelled Cheque / Bank Proof
          </Typography>
          <Input
            type="file"
            inputProps={{ accept: "image/*,.pdf" }}
            onChange={handleImageChange}
          />
        </Box>
      )}

      {!isNRI && selectedFile && (
        <Grid item style={{ marginTop: 16 }}>
          {selectedFile.type === "application/pdf" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, backgroundColor: "#ffebee", borderRadius: 1, border: "1px solid #ef9a9a" }}>
              <PictureAsPdfIcon sx={{ fontSize: 40, color: "#d32f2f" }} />
              <Typography variant="body2" color="text.secondary">PDF Selected: {selectedFile.name}</Typography>
            </Box>
          ) : (
            <img src={URL.createObjectURL(selectedFile)} alt="Preview" style={{ maxWidth: "100%", maxHeight: "200px" }} />
          )}
        </Grid>
      )}

      {!isNRI && !selectedFile && base64 && (
        <Grid item style={{ marginTop: 16 }}>
          {base64.startsWith("JVBERi0") ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.5, backgroundColor: "#ffebee", borderRadius: 1, border: "1px solid #ef9a9a" }}>
              <PictureAsPdfIcon sx={{ fontSize: 40, color: "#d32f2f" }} />
              <Typography variant="body2" color="text.secondary">PDF Document Uploaded</Typography>
            </Box>
          ) : (
            <img src={`data:image/png;base64,${base64}`} alt="Preview" style={{ maxWidth: "100%", maxHeight: "200px" }} />
          )}
        </Grid>
      )}

      {isNRI && (
        <>
          <FormControl variant="outlined" fullWidth margin="dense">
            <InputLabel id="nri-bank-type-label">NRI Bank Account Type</InputLabel>
            <Select labelId="nri-bank-type-label" value={currentState.nriBankAccountType} onChange={(e) => {
              const value = e.target.value;
              setCurrentState((prev) => ({ ...prev, nriBankAccountType: value, ...(value === "nro" ? { pisType: "" } : {}) }));
            }} label="NRI Bank Account Type">
              <MenuItem value="nro">NRO</MenuItem>
              <MenuItem value="nre">NRE</MenuItem>
            </Select>
          </FormControl>

          {currentState.nriBankAccountType === "nre" && (
            <FormControl variant="outlined" fullWidth margin="dense">
              <InputLabel id="pis-type-label">PIS Type</InputLabel>
              <Select labelId="pis-type-label" value={currentState.pisType} onChange={(e) => handleInputChange("pisType", e.target.value)} label="PIS Type">
                <MenuItem value="pis">PIS</MenuItem>
                <MenuItem value="non-pis">Non-PIS</MenuItem>
              </Select>
            </FormControl>
          )}

          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>Upload NRI Bank Proof</Typography>
            <Input type="file" inputProps={{ accept: "image/*,.pdf" }} onChange={handleNriProofChange} />

            {currentState.nriBankProofFilename && (
              <Box mt={1} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, backgroundColor: "#e8f5e9", borderRadius: 1, border: "1px solid #c8e6c9" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#1b5e20" }}>
                    {currentState.nriBankProofFilename}
                  </Typography>
                </Box>
                <Button variant="outlined" color="primary" size="small" startIcon={<GetAppIcon />} onClick={downloadNriProof} sx={{ ml: 2, minWidth: "auto" }}>
                  Download
                </Button>
              </Box>
            )}

            {currentState.nriBankProof && (
              <Typography variant="body2" style={{ marginTop: 8 }}>Selected File: {currentState.nriBankProof.name}</Typography>
            )}
          </Box>
        </>
      )}

      <Grid item style={{ marginTop: 24 }}>
        <Button variant="contained" color="primary" onClick={createCond ? addBankDetails : saveBankDetails} style={{ marginRight: "10px" }}>
          Submit
        </Button>

        {!createCond && (
          <Button
            variant="contained"
            color="secondary"
            onClick={deleteBankDetails}
          >
            Delete
          </Button>
        )}
      </Grid>
    </>
  );
};

export default BankDetailsInformationGrid;